import {
  isOutOfScope,
  type LoyaltyTaxOutput,
  type OverpaymentOutput,
  type OverpaymentResult,
} from "./types.js";

// Plain-English result copy for the energy overpayment check. Lives in the rules
// package so every surface (Expo result screen, "my results" list, PDF report)
// renders identical wording from one source — drift between channels is the
// information → advice kill-threat (validation §3).
//
// Regulatory guardrails baked into the wording:
//  - Information, not advice. We state the FACT — "your plan is $X vs the DMO
//    reference of $Y" — and stop. No "switch and save $X", no "you should
//    switch", no product/affiliate recommendation. The difference is presented as
//    the difference between two cited figures, never a "saving".
//  - We compare to the REGULATED REFERENCE PRICE (the DMO), and say so: retailers'
//    market offers move continuously, so we route to the government's free Energy
//    Made Easy as the authoritative next step (no affiliate in this copy).
//  - Every figure cites the AER + a verified-at date, with an "as at <date>,
//    verify with the AER / Energy Made Easy" caveat (ACL stale-figure mitigation —
//    the DMO reprices each 1 July, so the dated determination is named).

/**
 * The mandatory disclaimer rendered on every energy-overpayment surface
 * (CLAUDE.md regulatory guardrails / validation §3 — binding, kill-threat if
 * absent). Electricity retail is not a financial product, but the
 * information→advice line and the no-recommendation posture must be explicit, and
 * the "regulated reference, not a live market scrape" caveat is stated here.
 */
export const ENERGY_OVERPAYMENT_DISCLAIMER =
  "Educational tool. Not financial advice. This compares your plan to the AER's " +
  "regulated Default Market Offer reference price for your distribution zone — it " +
  "is not a live comparison of every retailer's market offer, and it is not a " +
  "recommendation to switch to any retailer or plan. Retailers' market offers " +
  "change continuously; the regulated reference price is set by the AER and " +
  "reprices each 1 July. For a free, official comparison using your actual usage, " +
  "use the government's Energy Made Easy (energymadeeasy.gov.au).";

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** A signed percentage to one decimal place, e.g. "12.3%". */
function formatPct(fraction: number): string {
  return `${(Math.abs(fraction) * 100).toFixed(1)}%`;
}

/** The copy line for Victoria (out of scope — VDO, not DMO). */
export function outOfScopeBullet(): string {
  return (
    "Victoria isn't covered by the Default Market Offer — Victorian prices use the " +
    "Essential Services Commission's Victorian Default Offer (VDO) instead. " +
    "For a free official comparison in Victoria, use Victorian Energy Compare " +
    "(compare.energy.vic.gov.au)."
  );
}

/**
 * Headline for an overpayment-vs-reference result. States the comparison as a
 * fact between two figures. Never says "save".
 */
export function overpaymentHeadline(result: OverpaymentResult): string {
  if (isOutOfScope(result)) return outOfScopeBullet();
  const out = result;
  if (out.band === "at_reference") {
    return `Your plan costs ${formatAud(out.currentAnnualCost)} a year — the same as the DMO reference price of ${formatAud(out.referencePrice)} for your zone.`;
  }
  const direction = out.band === "above_reference" ? "above" : "below";
  return `Your plan costs ${formatAud(out.currentAnnualCost)} a year — ${formatAud(Math.abs(out.differenceFromReference))} (${formatPct(out.percentFromReference)}) ${direction} the DMO reference price of ${formatAud(out.referencePrice)} for your zone.`;
}

/**
 * The factual comparison bullet. Describes which side of the regulated reference
 * the plan sits on, and (for the ACCC-flagged >10%-over cohort) notes that fact —
 * descriptively, with no "switch" nudge.
 */
export function overpaymentBullet(result: OverpaymentResult): string {
  if (isOutOfScope(result)) return outOfScopeBullet();
  const out = result;
  if (out.band === "below_reference") {
    return `At ${formatAud(out.currentAnnualCost)} a year, your plan is ${formatAud(Math.abs(out.differenceFromReference))} below the AER's regulated reference price of ${formatAud(out.referencePrice)} for your zone — your plan is priced under the regulated benchmark.`;
  }
  if (out.band === "at_reference") {
    return `At ${formatAud(out.currentAnnualCost)} a year, your plan is exactly at the AER's regulated reference price of ${formatAud(out.referencePrice)} for your zone.`;
  }
  const base = `At ${formatAud(out.currentAnnualCost)} a year, your plan is ${formatAud(out.differenceFromReference)} (${formatPct(out.percentFromReference)}) above the AER's regulated reference price of ${formatAud(out.referencePrice)} for your zone.`;
  if (out.materiallyAbove) {
    return `${base} That is more than 10% over the reference price — the cohort the ACCC flags as paying materially above the default offer.`;
  }
  return base;
}

/**
 * Loyalty-tax bullet — surfaced as a CITED RANGE of population averages, never a
 * personalised saving. States both ACCC figures with their basis/report year.
 */
export function loyaltyTaxBullet(out: LoyaltyTaxOutput): string {
  const lo = Math.min(out.lowerAnnual, out.upperAnnual);
  const hi = Math.max(out.lowerAnnual, out.upperAnnual);
  return (
    `The ACCC reports a "loyalty tax" on old plans: on average between ` +
    `${formatAud(lo)} and ${formatAud(hi)} a year more than customers on new plans, ` +
    `depending on how long the plan has been held — ${out.lowerBasis}; ${out.upperBasis}. ` +
    `This is an ACCC population average, not an estimate of your own bill.`
  );
}

/** Source-attribution line naming the dated AER determination in force. */
export function sourceBullet(
  determination: string,
  period: string,
  verifiedAt: string,
): string {
  return `Reference prices use the AER Default Market Offer (${determination}, ${period}), effective 1 July ${period.slice(0, 4)}, last verified ${verifiedAt}. The DMO reprices each 1 July — as at ${verifiedAt}, verify with the AER or Energy Made Easy (energymadeeasy.gov.au).`;
}

/** Full explanation block for an overpayment result. */
export function overpaymentExplanation(
  result: OverpaymentResult,
  determination: string,
  period: string,
  verifiedAt: string,
): string[] {
  return [
    overpaymentBullet(result),
    sourceBullet(determination, period, verifiedAt),
    ENERGY_OVERPAYMENT_DISCLAIMER,
  ];
}
